import { Duplexer } from '@wix/duplexer-js';
import { StartChatInitialData } from '@wix/wix-chatbot-common/browser';
import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';

import { clientSdk } from './common/ClientSDK';
import { DealerOfferListener } from './common/components/DealerOfferListener';
import { Loader } from './common/components/Loader';
import { useGetInitialChatbotState } from './common/hooks/useGetInitialChatbotState';
import { useGetRuntimeData } from './common/hooks/useGetRuntimeData';
import { useInitialParams } from './common/hooks/useInitialParams';
import { usePageEngagement } from './common/hooks/usePageEngagement';
import { useResetChatbotState } from './common/hooks/useResetChatbotState';
import { useWidgetSessionId } from './common/hooks/useWidgetSession';
import { baseURL, httpClient } from './common/httpClient';
import {
  DealerOfferParams,
  IChatSetChatbotDataLoading,
} from './common/reducers/types';
import { selectChatbotDataLoading } from './common/selectors/chat';
import { helpCenterService } from './common/services/helpCenter';
import { homepageService } from './common/services/homepage/homepageService';
import { loadHomepageContent } from './common/thunks/homepage.thunk';
import { ACTIONS } from './constants';
import { HelpCenter } from './help-center';
import {
  ChatbotMessageSubscriber,
  WidgetChannel,
  WidgetSessionSubscriber,
} from './messageChannel';
import { getInitialEntries } from './react-router/getInitialEntries';
import { emitter } from './util/emitter';
import { renderModel } from './util/renderModel';

const { duplexer, isWidget, userId, clientTopology } = renderModel.get();

const { messageChannels, duplexerURL } = duplexer;

// TODO: change the name
const duplexerInstance = new Duplexer(duplexerURL, {
  instanceUpdater: {
    getInstance: () => '',
  },
});

const setChatbotDataLoading = (
  payload: boolean,
): IChatSetChatbotDataLoading => ({
  type: ACTIONS.SET_CHATBOT_DATA_LOADING,
  payload,
});

export const App: FC = () => {
  const store = useStore();
  const dispatch = useDispatch();
  const { getInitialParams } = useInitialParams();
  const chatbotDataLoading = useSelector(selectChatbotDataLoading);
  const messageSubscriberRef = useRef<ChatbotMessageSubscriber | undefined>(
    undefined,
  );
  const widgetSessionSubscriberRef = useRef<
    WidgetSessionSubscriber | undefined
  >(undefined);
  const [routerInitialEntries, setRouterInitialEntries] = useState<
    string[] | undefined
  >(undefined);
  const {
    getInitialChatbotState,
    isParamsDifferent,
  } = useGetInitialChatbotState();
  const getRuntimeData = useGetRuntimeData();
  const resetChatbotState = useResetChatbotState();
  const { onMouseClick, onMouseMove } = usePageEngagement();

  const loadRuntimeData = useCallback(async () => {
    dispatch(setChatbotDataLoading(true));

    const {
      startChatInitialData,
      helpCenterSessionHistory,
    } = await getRuntimeData();
    resetChatbotState(startChatInitialData);

    const initialEntries = getInitialEntries(helpCenterSessionHistory);
    setRouterInitialEntries(initialEntries);

    dispatch(setChatbotDataLoading(false));
  }, [dispatch, getRuntimeData, resetChatbotState]);

  const fetchHomepageConfiguration = useCallback(
    async () => dispatch(loadHomepageContent()),
    [dispatch],
  );

  const loadStartChatInitialData = useCallback(
    async (params?: Map<string, string>) => {
      dispatch(setChatbotDataLoading(true));

      const { initialState } = await getInitialChatbotState(params);
      resetChatbotState(initialState as StartChatInitialData);

      dispatch(setChatbotDataLoading(false));
    },
    [dispatch, getInitialChatbotState, resetChatbotState],
  );

  const onDealerOfferParamsUpdate = useCallback(
    (params: DealerOfferParams) => {
      if (isParamsDifferent(params)) {
        void loadStartChatInitialData(params);
      }
    },
    [isParamsDifferent, loadStartChatInitialData],
  );

  useEffect(() => {
    getInitialParams().then((initialParams) =>
      httpClient.getInstance(initialParams),
    );
  }, []);

  // get sessionId and store it on widget session reducer
  useWidgetSessionId();

  // create channels
  useEffect(() => {
    const widgetCommunication = isWidget ? new WidgetChannel(store) : undefined;
    messageSubscriberRef.current = new ChatbotMessageSubscriber(
      duplexerInstance,
      messageChannels?.chatbotMessage,
      store,
      isWidget,
    );
    widgetSessionSubscriberRef.current = new WidgetSessionSubscriber(
      duplexerInstance,
      messageChannels?.widgetSession,
    );

    emitter.on('forceLoadInitialChatbotState', () =>
      loadStartChatInitialData(),
    );

    const unsubscribeContextUpdate =
      userId && isWidget
        ? clientSdk.subscribeToContextUpdate(
            baseURL + clientTopology.contextURL,
          )
        : undefined;

    void loadRuntimeData();
    if (homepageService.homepageEnabled && !helpCenterService.isFullWidth) {
      void fetchHomepageConfiguration();
    }

    return () => {
      messageSubscriberRef.current?.unsubscribe();
      widgetSessionSubscriberRef.current?.unsubscribe();
      widgetCommunication?.unsubscribe();
      unsubscribeContextUpdate && unsubscribeContextUpdate();
      emitter.remove('forceLoadInitialChatbotState');
    };
  }, []);

  useEffect(() => {
    if (!messageSubscriberRef.current) {
      return;
    }
    messageSubscriberRef.current.forceIgnoreMessages = !!chatbotDataLoading;
  }, [chatbotDataLoading, messageSubscriberRef.current]);

  if (!routerInitialEntries) {
    return (
      <Loader
        height="100%"
        width="100%"
        position="absolute"
        verticalAlign="middle"
      />
    );
  }

  return (
    <DealerOfferListener onDealerOfferParamsUpdate={onDealerOfferParamsUpdate}>
      <div onMouseMove={onMouseMove} onClick={onMouseClick}>
        <HelpCenter routerInitialEntries={routerInitialEntries} />
      </div>
    </DealerOfferListener>
  );
};
